// app/javascript/controllers/generic_sortable_controller.js
import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

export default class extends Controller {
  static targets = ["item"]
  static values = {
    url: String,
    itemType: String  // e.g., "review", "photo", "task", etc.
  }

  connect() {
    if (!this.element) return

    this.sortable = Sortable.create(this.element, {
      animation: 150,
      handle: '.drag-handle',
      ghostClass: 'sortable-ghost',
      onEnd: this.updateOrder.bind(this)
    })
  }

  disconnect() {
    if (this.sortable) {
      this.sortable.destroy()
    }
  }

  updateOrder() {
    // Get current items fresh from the DOM (don't rely on cached targets)
    const currentItems = this.element.querySelectorAll('[data-generic-sortable-target="item"]')
    
    if (currentItems.length === 0) {
      console.warn("Generic sortable controller missing item targets.")
      return
    }

    // Get IDs from data-item-id attribute
    const itemIds = Array.from(currentItems).map(item => {
      const id = item.dataset.itemId
      if (!id) {
        console.warn("Item missing data-item-id attribute:", item)
      }
      return id
    }).filter(id => id) // Remove any undefined/null IDs
    

    // Update position fields in the form (if they exist)
    Array.from(currentItems).forEach((item, index) => {
      const positionField = item.querySelector('.position-field')
      if (positionField) {
        positionField.value = index
      }
    })

    // If this is a new form (no URL), we don't need to make an AJAX request
    if (!this.urlValue) {
      return
    }

    // Prepare data using FormData
    const formData = new FormData()
    const itemType = this.itemTypeValue || 'item'
    
    itemIds.forEach((id, index) => {
      formData.append(`${itemType}_ids[]`, id)
      formData.append('positions[]', index)
    })

    // Get CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    if (!csrfToken) {
      console.error("CSRF token not found!")
      return
    }


    fetch(this.urlValue, {
      method: 'PATCH',
      headers: {
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json'
      },
      body: formData
    })
    .then(response => {
      if (response.ok) {
        this.showToast()
      } else {
        response.text().then(text => 
          console.error('Error updating positions:', response.status, text)
        )
      }
    })
    .catch(error => {
      console.error('Network or other error updating positions:', error)
    })
  }

  showToast() {
    const itemType = this.itemTypeValue || 'Item'
    const message = `${itemType.charAt(0).toUpperCase() + itemType.slice(1)} order updated!`

    // Check if Bootstrap is available
    if (typeof bootstrap === 'undefined' || typeof bootstrap.Toast === 'undefined') {
      return
    }

    // Create or get toast container
    let toastContainer = document.getElementById('sortable-toast-container')
    if (!toastContainer) {
      toastContainer = document.createElement('div')
      toastContainer.id = 'sortable-toast-container'
      toastContainer.className = 'position-fixed bottom-0 end-0 p-3'
      toastContainer.style.zIndex = '1056'
      document.body.appendChild(toastContainer)
    }

    // Create the toast element
    const toastElement = document.createElement('div')
    toastElement.className = 'toast align-items-center text-white bg-success border-0'
    toastElement.setAttribute('role', 'alert')
    toastElement.setAttribute('aria-live', 'assertive')
    toastElement.setAttribute('aria-atomic', 'true')
    toastElement.innerHTML = `
      <div class="d-flex">
        <div class="toast-body">
          ${message}
        </div>
        <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast" aria-label="Close"></button>
      </div>
    `

    toastContainer.appendChild(toastElement)

    const toast = new bootstrap.Toast(toastElement, { delay: 3000 })
    toast.show()

    // Clean up toast after hiding
    toastElement.addEventListener('hidden.bs.toast', () => {
      toastElement.remove()
    })
  }
}